/**
 * @fileOverview Custom hook to manage table locale synchronization with i18next language changes
 * @module useTableLocale
 */

import { useAtom } from 'jotai';
import { tableLocaleAtom, getLocaleFromLanguage } from '../stores/tableLocaleAtom';
import { useEffect } from 'react';
import i18next from 'i18next';

/**
 * Custom hook that synchronizes table locale with the current i18next language
 * @returns {Object} The current locale configuration for data grid tables
 * 
 * @example
 * const locale = useTableLocale();
 * // Use locale in DataGridPro component
 * <DataGridPro localeText={locale.components.MuiDataGrid.defaultProps.localeText} />
 */
export const useTableLocale = () => {
  const [locale, setLocale] = useAtom(tableLocaleAtom);

  useEffect(() => {
    /**
     * Handler function that updates the table locale when language changes
     */
    const handleLanguageChange = () => {
      setLocale(getLocaleFromLanguage(i18next.language));
    };

    // Subscribe to language change events
    i18next.on('languageChanged', handleLanguageChange);
    
    // Cleanup subscription on unmount
    return () => {
      i18next.off('languageChanged', handleLanguageChange);
    };
  }, [setLocale]);

  return locale;
};